import React from "react";

const PrivacyContent: React.FC = () => (
  <div className="bg-white p-4 p-md-5 rounded shadow-sm">
    <h1 className="h3 mb-2">Privacy Policy for Send Updates AI</h1>
    <p className="text-muted mb-4">
      <strong>Last Updated:</strong> August 18, 2025
    </p>

    <h2 className="h5 mt-4">1. Introduction</h2>
    <p>
      Send Updates AI (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;)
      operates a platform that integrates with third-party services through
      OAuth connections to aggregate data, generate insights using artificial
      intelligence, and automate email communications. We are committed to
      protecting your privacy and handling your data responsibly.
    </p>
    <p>
      This Privacy Policy explains how we collect, use, disclose, and safeguard
      your information when you use our service.
    </p>

    <h2 className="h5 mt-4">2. Information We Collect</h2>
    <h3 className="h6 mt-3">2.1 Account Information</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Name and email address</li>
      <li>Account credentials and authentication tokens</li>
      <li>Profile information you choose to provide</li>
      <li>
        Billing and payment information (processed through third-party payment
        processors)
      </li>
    </ul>
    <h3 className="h6 mt-3">2.2 OAuth Integration Data</h3>
    <p>
      When you connect third-party services through OAuth, we may access and
      collect:
    </p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Data from connected services as permitted by your OAuth authorizations
      </li>
      <li>Service usage patterns and metadata</li>
      <li>Integration health and status information</li>
      <li>Data necessary to provide insights and automation features</li>
    </ul>
    <h3 className="h6 mt-3">2.3 Platform Usage Data</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Log files including IP addresses, browser types, and access times</li>
      <li>Device information and operating system details</li>
      <li>Feature usage patterns and user interactions</li>
      <li>Email delivery metrics and engagement data</li>
      <li>Performance and error reporting data</li>
    </ul>
    <h3 className="h6 mt-3">2.4 AI Processing Data</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Data processed by our AI systems to generate insights</li>
      <li>Generated summaries, insights, and recommendations</li>
      <li>Email content created through our automation features</li>
      <li>Model training data (aggregated and anonymized)</li>
    </ul>

    <h2 className="h5 mt-4">3. How We Use Your Information</h2>
    <h3 className="h6 mt-3">3.1 Core Service Provision</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Authenticate and authorize access to integrated services</li>
      <li>Aggregate and analyze data from your connected services</li>
      <li>Generate AI-powered insights and recommendations</li>
      <li>Create and send automated email updates</li>
      <li>Maintain and improve service functionality</li>
    </ul>
    <h3 className="h6 mt-3">3.2 Communication and Support</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Provide customer support and technical assistance</li>
      <li>Send service-related notifications and updates</li>
      <li>Communicate about new features and improvements</li>
      <li>Process billing and account management requests</li>
    </ul>
    <h3 className="h6 mt-3">3.3 Legal and Security</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Comply with legal obligations and regulatory requirements</li>
      <li>Protect against fraud, abuse, and security threats</li>
      <li>Enforce our Terms of Use and other policies</li>
      <li>Respond to law enforcement and legal requests</li>
    </ul>

    <h2 className="h5 mt-4">4. Data Sharing and Disclosure</h2>
    <h3 className="h6 mt-3">4.1 Third-Party Services</h3>
    <p>
      We share data with third parties only as necessary to provide our
      services:
    </p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        <strong>OAuth Providers:</strong> Authentication and authorization data
        as required by OAuth protocols
      </li>
      <li>
        <strong>Email Service Providers:</strong> Email content and recipient
        information for delivery
      </li>
      <li>
        <strong>Cloud Infrastructure:</strong> Data processing and storage
        through secure cloud services
      </li>
      <li>
        <strong>Analytics Providers:</strong> Aggregated, anonymized usage data
        for service improvement
      </li>
    </ul>
    <h3 className="h6 mt-3">4.2 Legal Requirements</h3>
    <p>We may disclose information when required by law or to:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Comply with legal process, court orders, or regulatory requirements
      </li>
      <li>Protect our rights, property, or safety, or that of our users</li>
      <li>Investigate fraud, security issues, or terms violations</li>
      <li>Facilitate business transfers or asset sales</li>
    </ul>
    <h3 className="h6 mt-3">4.3 Business Transfers</h3>
    <p>
      In the event of a merger, acquisition, or sale of assets, user information
      may be transferred as part of the transaction, subject to appropriate
      privacy protections.
    </p>

    <h2 className="h5 mt-4">5. Data Security and Protection</h2>
    <h3 className="h6 mt-3">5.1 Security Measures</h3>
    {/* <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>End-to-end encryption for data in transit and at rest</li>
      <li>Multi-factor authentication and access controls</li>
      <li>Regular security audits and penetration testing</li>
      <li>Employee security training and background checks</li>
      <li>Incident response and breach notification procedures</li>
    </ul> */}
    <h3 className="h6 mt-3">5.1 OAuth Security</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Secure token storage and management</li>
      <li>Regular token refresh and validation</li>
      <li>Scope limitation and permission reviews</li>
      <li>Secure credential transmission and storage</li>
    </ul>
    <h3 className="h6 mt-3">5.2 Data Retention</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Account data retained while your account is active</li>
      <li>
        Integration data retained according to your settings and legal
        requirements
      </li>
      <li>Log data typically retained for 12 months</li>
      <li>Backup data securely deleted within defined timeframes</li>
      <li>
        Right to request earlier deletion subject to legal and operational
        requirements
      </li>
    </ul>

    <h2 className="h5 mt-4">6. International Data Transfers</h2>
    <p>
      We may transfer and process your data in countries other than your own.
      When we do:
    </p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>We ensure adequate protection through appropriate safeguards</li>
      <li>We comply with applicable data protection laws and regulations</li>
      <li>
        We use Standard Contractual Clauses or other approved transfer
        mechanisms
      </li>
      <li>We provide notice of significant changes to transfer arrangements</li>
    </ul>

    <h2 className="h5 mt-4">7. Your Privacy Rights</h2>
    <h3 className="h6 mt-3">7.1 Access and Control</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        <strong>Access:</strong> Request copies of your personal data
      </li>
      <li>
        <strong>Rectification:</strong> Correct inaccurate or incomplete
        information
      </li>
      <li>
        <strong>Erasure:</strong> Request deletion of your data (subject to
        legal limitations)
      </li>
      <li>
        <strong>Portability:</strong> Receive your data in a structured,
        machine-readable format
      </li>
      <li>
        <strong>Restriction:</strong> Limit how we process your data in certain
        circumstances
      </li>
    </ul>
    <h3 className="h6 mt-3">7.2 Communication Preferences</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Opt out of marketing communications</li>
      <li>Control notification settings</li>
      <li>Manage email automation preferences</li>
      <li>Update contact preferences</li>
    </ul>
    <h3 className="h6 mt-3">7.3 OAuth Management</h3>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Review and revoke OAuth connections</li>
      <li>Modify data access permissions</li>
      <li>Export data from integrated services</li>
      <li>Control data sharing settings</li>
    </ul>

    <h2 className="h5 mt-4">8. Cookies and Tracking</h2>
    <p>We use cookies and similar technologies to:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Authenticate users and maintain sessions</li>
      <li>Remember preferences and settings</li>
      <li>Analyze usage patterns and improve our service</li>
      <li>Provide personalized experiences</li>
    </ul>
    <p>You can control cookie settings through your browser preferences.</p>

    <h2 className="h5 mt-4">9. Children&apos;s Privacy</h2>
    <p>
      Our service is not intended for children under 16. We do not knowingly
      collect personal information from children. If we become aware of such
      collection, we will delete the information promptly.
    </p>

    <h2 className="h5 mt-4">10. California Privacy Rights (CCPA)</h2>
    <p>California residents have additional rights including:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Right to know what personal information is collected</li>
      <li>Right to delete personal information</li>
      <li>Right to opt-out of the sale of personal information</li>
      <li>Right to non-discrimination for exercising privacy rights</li>
    </ul>
    <p>We do not sell personal information as defined by the CCPA.</p>

    <h2 className="h5 mt-4">11. European Privacy Rights (GDPR)</h2>
    <p>
      If you are in the European Economic Area, you have additional rights under
      GDPR:
    </p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>
        Lawful basis for processing (legitimate interest, consent, or contract
        performance)
      </li>
      <li>Right to object to processing</li>
      <li>Right to file complaints with supervisory authorities</li>
      <li>Data Protection Officer contact information: legal@sendupdates.ai</li>
    </ul>

    <h2 className="h5 mt-4">12. Changes to Privacy Policy</h2>
    <p>We may update this Privacy Policy periodically. We will:</p>
    <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
      <li>Notify you of material changes via email or platform notification</li>
      <li>Post the updated policy with a new effective date</li>
      <li>Maintain previous versions for reference</li>
      <li>Provide reasonable time to review changes before they take effect</li>
    </ul>

    <h2 className="h5 mt-4">13. Contact Information</h2>
    <p>For privacy-related questions, requests, or concerns:</p>
    <p>
      <strong>Send Updates AI Privacy Team</strong>
      <br />
      Email: privacy@sendupdates.ai
      {/* <br />
      Address: [Physical Address]
      <br />
      Phone: [Phone Number] */}
    </p>
    {/* <p>
      <strong>Data Protection Officer</strong> (if applicable):
      <br />
      Email: dpo@sendupdates.ai
    </p> */}

    <h2 className="h5 mt-4">14. Dispute Resolution</h2>
    <p>
      We are committed to resolving privacy disputes promptly and fairly. If you
      have concerns:
    </p>
    <ol style={{ listStyleType: "decimal", paddingLeft: "1.5rem" }}>
      <li>Contact our Privacy Team first</li>
      <li>We will investigate and respond within 30 days</li>
      <li>
        For unresolved issues, you may contact relevant supervisory authorities
      </li>
      <li>Binding arbitration may be available for certain disputes</li>
    </ol>

    <hr />
    <p>
      <em>
        This Privacy Policy is effective as of August 18, 2025 and applies to
        all users of the Send Updates AI platform.
      </em>
    </p>
  </div>
);

export default PrivacyContent;
